import styled from 'styled-components';
import { Routes, Route, Navigate, useMatch, useParams } from 'react-router-dom';

import {
  OptionsRouteParams,
  OPTIONS_ASSET_ROUTE,
  OPTIONS_ROUTE,
  OPTIONS_ASSET_SEGMENT,
  OPTIONS_EXPIRY_DATE_SEGMENT,
} from '#/routes';
import { useMarketsRouting } from '#/features/options/markets-routing-context';
import { Error } from '#/components/options/atoms';
import { MarketSummariesStateProvider } from '#/features/options/market-summaries-context';
import { OptionMarketsStateProvider } from '#/features/options/markets-context';
import { OptionTablesProvider } from '#/features/options/option-tables-context';
import { OrderModalProvider } from '#/features/options/order-modal/order-modal-context';
import OrderModal from '#/components/options/order-modal/OrderModal';
import { c080820 } from '#/colors';

import MarketInfo from './market-info/MarketInfo';
import OptionTables from './option-tables/OptionTables';
import TradeOverview from './trade-overview/TradeOverview';

export default function Options() {
  // WIP: to be removed on Options release
  return (
    <div
      css={`
        display: grid;
        place-items: center;
        height: 100%;
        opacity: 0.4;
        font-size: 24px;
      `}
    >
      Coming Soon…
    </div>
  );

  // @ts-ignore
  return (
    <OptionMarketsStateProvider>
      <MarketSummariesStateProvider>
        <OrderModalProvider>
          <OrderModal />
          <OptionTablesProvider>
            <OptionsRouting />
          </OptionTablesProvider>
        </OrderModalProvider>
      </MarketSummariesStateProvider>
    </OptionMarketsStateProvider>
  );
}

function OptionsRouting() {
  const marketsRouting = useMarketsRouting();

  if (marketsRouting.status === 'error') {
    return <Error>{marketsRouting.error}</Error>;
  }

  if (marketsRouting.status === 'loading') {
    return null;
  }

  return <AssetRouting />;
}

function AssetRouting() {
  const routeMatch = useMatch(`${OPTIONS_ROUTE}/*`);
  const marketsRouting = useMarketsRouting();

  if (routeMatch == null) {
    return <Error>{`Failed to match route '${OPTIONS_ROUTE}'`}</Error>;
  }

  if (marketsRouting.defaultAsset == null) {
    return <Error>No assets available</Error>;
  }

  return (
    <Routes>
      <Route
        path={`${OPTIONS_ASSET_SEGMENT}/*`}
        element={<ExpiryDateRouting />}
      />
      <Route
        path="*"
        element={<Navigate to={marketsRouting.defaultAsset} replace />}
      />
    </Routes>
  );
}

function ExpiryDateRouting() {
  const routeMatch = useMatch(`${OPTIONS_ASSET_ROUTE}/*`);
  const marketsRouting = useMarketsRouting();

  if (routeMatch == null) {
    return <Error>{`Failed to match route '${OPTIONS_ASSET_ROUTE}'`}</Error>;
  }

  if (marketsRouting.defaultExpiryDate == null) {
    return (
      <Error>{`Asset '${marketsRouting.defaultAsset}' does not have active options`}</Error>
    );
  }

  return (
    <Routes>
      <Route
        path={`${OPTIONS_EXPIRY_DATE_SEGMENT}/*`}
        element={<UnknownExpiryDateRouting />}
      />
      <Route
        path="*"
        element={<Navigate to={marketsRouting.defaultExpiryDate} replace />}
      />
    </Routes>
  );
}

function UnknownExpiryDateRouting() {
  const routeParams = useParams<OptionsRouteParams>();
  const marketsRouting = useMarketsRouting();
  const expiryDates =
    routeParams.asset != null
      ? marketsRouting.expiryDates[routeParams.asset] ?? []
      : [];

  if (
    routeParams.expiryDate == null ||
    !expiryDates.includes(routeParams.expiryDate)
  ) {
    return <Navigate to={OPTIONS_ROUTE} replace />;
  }

  return <OptionsContent />;
}

function OptionsContent() {
  return (
    <Container>
      <MarketInfo css="grid-area: a1" />
      <OptionTables css="grid-area: a2" />
      <TradeOverview css="grid-area: a3" />
    </Container>
  );
}

const Container = styled.div`
  flex-grow: 1;
  display: grid;
  background-color: ${c080820};
  grid-template-rows: 60px 1fr 240px;
  grid-template-areas:
    'a1'
    'a2'
    'a3';
`;
